import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getStructure,
  updateStructure,
  UpdateStructurePayload,
  ReportSection,
} from "../../services/mdtReportStructure.service";
import Modal from "../../components/common/Modal";
import ConfirmModal from "../../components/common/ConfirmModal";
import MdtReportPreview, { PreviewBranding } from "./components/MdtReportPreview";
import {
  ClipboardList,
  Eye,
  Loader2,
  Save,
  ChevronUp,
  ChevronDown,
  X,
  Plus,
  FileText,
} from "lucide-react";

type Tab = "edit" | "preview";

const SAMPLE_HOSPITAL: PreviewBranding = {
  logoUrl: null,
  hospitalName: "Sample Hospital",
  addressSnapshot: "1 Test Street, London",
  contactSnapshot: "sample@hospital.co.uk",
  signatureLeftLabel: "Lead Oncologist Signature",
  signatureRightLabel: "MDT Coordinator Verification",
  footerText: "",
};

const MdtStructureEditor = () => {
  const [form, setForm] = useState<UpdateStructurePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("edit");
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionRequired, setNewSectionRequired] = useState(true);
  const [removeTarget, setRemoveTarget] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const s = await getStructure();
      setForm({
        reportTitle: s.reportTitle,
        reportIdPrefix: s.reportIdPrefix,
        sections: (s.sections || []).map((v) => ({ ...v })),
        patientInfoFields: (s.patientInfoFields || []).map((f) => ({ ...f })),
      });
    } catch {
      toast.error("Failed to load report structure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => (prev ? { ...prev, [name]: value } : prev));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSectionChange = (index: number, field: keyof ReportSection, value: any) => {
    setForm((prev) => {
      if (!prev) return prev;
      const currentSections = prev.sections || [];
      const newSections = [...currentSections];
      newSections[index] = { ...newSections[index], [field]: value };
      return { ...prev, sections: newSections };
    });
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    setForm((prev) => {
      if (!prev) return prev;
      const sections = prev.sections || [];
      const target = index + direction;
      if (target < 0 || target >= sections.length) return prev;
      const newSections = [...sections];
      const [item] = newSections.splice(index, 1);
      newSections.splice(target, 0, item);
      return { ...prev, sections: newSections };
    });
  };

  const confirmAddSection = () => {
    setForm((prev) => {
      if (!prev) return prev;
      const sections = prev.sections || [];
      const title = newSectionTitle.trim() || "New section";
      const key = `custom_${Date.now().toString(36)}_${sections.length + 1}`;
      const next: ReportSection = {
        key,
        displayTitle: title,
        order: sections.length + 1,
        required: newSectionRequired,
        enabled: true,
      };
      return { ...prev, sections: [...sections, next] };
    });
    setAddSectionOpen(false);
  };

  const removeSection = (index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      const sections = (prev.sections || [])
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, order: i + 1 }));
      return { ...prev, sections };
    });
    setRemoveTarget(null);
  };

  const togglePatientField = (key: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const fields = prev.patientInfoFields || [];
      const newFields = fields.map((f) =>
        f.key === key && !f.locked ? { ...f, enabled: !f.enabled } : f
      );
      return { ...prev, patientInfoFields: newFields };
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form?.reportTitle?.trim()) errs.reportTitle = "Report title is required";

    const sections = form?.sections || [];
    if (sections.length === 0) errs.sections = "At least one section is required.";
    const requiredDisabled = sections.filter((s) => s.required === true && s.enabled === false);
    if (requiredDisabled.length > 0) {
      errs.sections = `Required sections must remain enabled: ${requiredDisabled
        .map((s) => s.key)
        .join(", ")}`;
    }
    const lockedDisabled = (form?.patientInfoFields || []).filter((f) => f.locked && f.enabled === false);
    if (lockedDisabled.length > 0) {
      errs.patientInfoFields = "Locked patient fields must remain enabled.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!form) return;
    setSaving(true);
    try {
      const updated = await updateStructure({
        reportTitle: form.reportTitle,
        reportIdPrefix: form.reportIdPrefix,
        sections: form.sections,
        patientInfoFields: form.patientInfoFields,
      });
      setForm({
        reportTitle: updated.reportTitle,
        reportIdPrefix: updated.reportIdPrefix,
        sections: (updated.sections || []).map((s) => ({ ...s })),
        patientInfoFields: (updated.patientInfoFields || []).map((f) => ({ ...f })),
      });
      toast.success("Report structure saved");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save report structure");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
            Report structure
          </h1>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:w-auto w-full"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save changes
        </button>
      </div>

      {/* Edit / Preview sub-tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        <TabButton active={activeTab === "edit"} onClick={() => setActiveTab("edit")}>
          <ClipboardList size={16} />
          Edit
        </TabButton>
        <TabButton active={activeTab === "preview"} onClick={() => setActiveTab("preview")}>
          <Eye size={16} />
          Preview
        </TabButton>
      </div>

      {activeTab === "edit" ? (
        <div className="space-y-6">
          <Card icon={<FileText size={18} />} title="Report header">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Report title"
                name="reportTitle"
                value={form.reportTitle || ""}
                onChange={handleChange}
                placeholder="MULTIDISCIPLINARY TEAM CLINICAL REPORT"
                error={errors.reportTitle}
              />
              <Field
                label="Report ID prefix"
                name="reportIdPrefix"
                value={form.reportIdPrefix || ""}
                onChange={handleChange}
                placeholder="Report ID: #"
              />
            </div>
          </Card>

          <Card
            icon={<FileText size={18} />}
            title="Patient details block"
            description="Choose which patient/meeting fields appear at the top of the report. Values are pulled from the case record at generation time — not entered here."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {(form.patientInfoFields || []).map((field) => (
                <label
                  key={field.key}
                  className={`flex items-center gap-2.5 text-sm cursor-pointer ${
                    field.locked ? "cursor-not-allowed" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={field.locked || field.enabled}
                    disabled={field.locked}
                    onChange={() => togglePatientField(field.key)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                  />
                  <span className={field.locked ? "text-gray-700" : "text-gray-800"}>
                    {field.label}
                  </span>
                  {field.locked && <span className="text-xs text-gray-400">(always shown)</span>}
                </label>
              ))}
            </div>
            {errors.patientInfoFields && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">
                {errors.patientInfoFields}
              </div>
            )}
          </Card>

          <Card
            icon={<ClipboardList size={18} />}
            title="Report sections"
            description="Section content is generated per patient — choose which sections appear, their order, and their displayed title. The standard sections are all required; custom sections added below can be required or optional."
          >
            {errors.sections && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">
                {errors.sections}
              </div>
            )}
            <div className="space-y-3">
              {(form.sections || []).map((section, index) => {
                return (
                  <div key={section.key || index} className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                    <div className="flex items-center flex-wrap gap-3">
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveSection(index, -1)}
                          disabled={index === 0}
                          className="h-6 w-6 rounded flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSection(index, 1)}
                          disabled={index === (form.sections || []).length - 1}
                          className="h-6 w-6 rounded flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>

                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={section.displayTitle}
                          onChange={(e) => handleSectionChange(index, "displayTitle", e.target.value)}
                          placeholder="Section title"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      <label
                        className="flex items-center gap-1.5 text-sm text-gray-600 cursor-not-allowed"
                        title="This section is locked as required"
                      >
                        <input
                          type="checkbox"
                          checked={section.required === true}
                          disabled={true}
                          onChange={() => undefined}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                        />
                        Required
                      </label>

                      <button
                        type="button"
                        onClick={() => setRemoveTarget(index)}
                        title="Remove section"
                        aria-label="Remove section"
                        className="h-8 w-8 rounded-md flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => {
                setNewSectionTitle("");
                setNewSectionRequired(true);
                setAddSectionOpen(true);
              }}
              className="mt-4 inline-flex items-center gap-1.5 w-full justify-center border border-dashed border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            >
              <Plus size={16} />
              Add section
            </button>
          </Card>
        </div>
      ) : (
        <MdtReportPreview branding={SAMPLE_HOSPITAL} structure={form} />
      )}

      <Modal
        isOpen={addSectionOpen}
        title="Add section"
        subtitle="Add a custom section to the report"
        onClose={() => setAddSectionOpen(false)}
      >
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1.5">Section title</span>
            <input
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              placeholder="e.g. Radiology Review"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <span className="block mt-1 text-xs text-gray-400">Leave blank to use "New section".</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={newSectionRequired}
              onChange={(e) => setNewSectionRequired(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Required — always include this section in generated reports
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setAddSectionOpen(false)}
              className="px-4 py-2 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={confirmAddSection}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors text-sm font-medium"
            >
              Add Section
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={removeTarget !== null}
        title="Remove section"
        message={
          removeTarget !== null
            ? `Remove "${(form.sections || [])[removeTarget]?.displayTitle || "this section"}" from the report structure? This cannot be undone.`
            : ""
        }
        confirmText="Remove"
        variant="danger"
        onConfirm={() => removeTarget !== null && removeSection(removeTarget)}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

// --- Helpers ---

const Card = ({
  icon,
  title,
  description,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
    <div className="px-6 pt-5 pb-4 border-b border-gray-100">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Field = ({
  label,
  name,
  value,
  onChange,
  error,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
      active
        ? "border-primary-600 text-primary-700"
        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

export default MdtStructureEditor;